import React from 'react'
import { BpAnchor } from '../../../../shared/components/text/anchor/BpAnchor'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import { IconVerified } from '../../../../shared/components/icons/IconVerified'
import { Popover } from '../../../../lib/ui/popover/component/Popover'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { showAlert } from '../../../../app/store/reducers/AppSlice'
import { IconCopy } from '../../../../shared/components/icons/IconCopy'
import clsx from 'clsx'
import styles from '../../widgets/link-list/LinksList.module.scss'
import { AdvertiserCard } from '../../../../app/api'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'

interface DetailsCellProps {
  countries: string | string[]
  cities: string | string[]
  engines: string | string[]
  devices: string
  search_screen_url: string | null
  link_screen_url: string | null
  affiliate_id?: string | null
  advertiser?: AdvertiserCard | null
}

export const DetailsCell = ({
  engines,
  search_screen_url,
  link_screen_url,
  cities,
  countries,
  devices,
  affiliate_id,
  advertiser,
}: DetailsCellProps) => {
  const { t } = useTranslation('translation')
  const dispatch = useAppDispatch()

  return (
    <ul className='flex flex-col'>
      {engines && (
        <li className='flex gap-[5px] text-[15px] leading-[24px]'>
          <span className='text-aqua-300'>{t('components.detailsCell.searchEngine')}:</span>
          <span className='text-focus-600'>{engines}</span>
        </li>
      )}
      {(search_screen_url || link_screen_url) && (
        <li className='flex gap-[5px] text-[15px] leading-[24px]'>
          <span className='text-aqua-300'>{t('components.detailsCell.screenshots')}:</span>
          <span className='text-focus-600'>
            {search_screen_url && (
              <BpAnchor className={'whitespace-nowrap'} href={search_screen_url} target='_blank'>
                {t('components.detailsCell.SERP')}
              </BpAnchor>
            )}
            {search_screen_url && link_screen_url && ', '}
            {link_screen_url && (
              <BpAnchor className={'whitespace-nowrap'} href={link_screen_url} target='_blank'>
                {t('components.detailsCell.landingPage')}
              </BpAnchor>
            )}
          </span>
        </li>
      )}
      {affiliate_id && (
        <li className='flex gap-[5px] text-[15px] leading-[24px]'>
          <span className='text-aqua-300'>{t('components.detailsCell.affiliate')}:</span>
          <Tooltip title={affiliate_id}>
            <span className='max-w-[175px] truncate text-focus-600'>{affiliate_id}</span>
          </Tooltip>
        </li>
      )}
      <li className='mt-[8px]'>
        <span className='flex gap-[5px] text-[13px] leading-[16px] text-aqua-300'>
          {(cities || countries) && (
            <span>
              {cities}
              <br />
              {countries}
            </span>
          )}
          |{devices && <span>{devices}</span>}
        </span>
      </li>
      {advertiser ? (
        <li className={'mt-[8px]'}>
          <div className={'flex gap-[4px]'}>
            <Popover
              trigger={() => (
                <p className={'max-w-[200px] cursor-pointer truncate'}>
                  <span
                    className={
                      'cursor-pointer border-b border-dotted text-[13px] leading-[13px] text-focus'
                    }
                  >
                    {advertiser.name}
                  </span>
                </p>
              )}
            >
              <div className={'flex w-[350px] flex-col gap-[4px]'}>
                <div className={'flex items-center gap-[4px]'}>
                  <p className={'text-[14px] leading-[20px]'}>
                    {t('components.detailsCell.advertiserName')}{' '}
                    <Tooltip title={advertiser.name}>
                      <span className={'font-medium'}>{advertiser.name}</span>
                    </Tooltip>
                  </p>
                  <CopyToClipboard
                    text={advertiser.name ?? ''}
                    onCopy={() =>
                      dispatch(
                        showAlert({
                          message: t('notifications.copiedToClipboard'),
                          type: 'success',
                          timeout: 2000,
                        }),
                      )
                    }
                  >
                    <button className={'h-[14px] w-[14px]'}>
                      <IconCopy className={clsx(styles.copyIcon)} color='#0e63d8' />
                    </button>
                  </CopyToClipboard>
                </div>
                <p className={'text-[14px] leading-[20px]'}>
                  {t('components.detailsCell.location')}{' '}
                  <span className={'font-medium'}>{advertiser.location}</span>
                </p>
                {advertiser.verified && (
                  <p className={'text-[14px] leading-[20px]'}>
                    {t('components.detailsCell.verifiedBy')}{' '}
                    <span className={'font-medium'}>{advertiser.verified_by}</span>
                    <span>
                      <IconVerified className='ml-1 inline w-[14px]' color='#00BD7B' />
                    </span>
                  </p>
                )}
                <p className={'text-[14px] leading-[20px]'}>
                  {t('components.detailsCell.adsTransparencyCenter')}:{' '}
                  <a
                    className={'font-medium text-focus'}
                    href={advertiser.transparency_url}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {advertiser.transparency_url?.includes('google') ? 'Google' : 'Bing'}
                  </a>
                </p>
              </div>
            </Popover>
            {advertiser.verified && (
              <Tooltip
                title={t('pages.paidResults.tooltipVerifiedByFallback', {
                  name: advertiser.verified_by,
                })}
              >
                <span>
                  <IconVerified className='ml-1 mb-0.5 inline w-[16px]' color='#00BD7B' />
                </span>
              </Tooltip>
            )}
          </div>
        </li>
      ) : (
        <li className={'mt-[8px]'}>
          <div className={'flex items-center gap-[4px]'}>
            <p>N/A</p>
            <BpTooltip title={t('components.detailsCell.notAvailableTooltip')} />
          </div>
        </li>
      )}
    </ul>
  )
}
